import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usersAPI } from '../services/api';
import { Button } from '../components/Common/Button';
import { Card } from '../components/Common/Card';
import { Select } from '../components/Common/Select';
import { Label } from '../components/Common/Label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/Common/Dialog';
import { Edit, Trash2, Users } from 'lucide-react';

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editedStatus, setEditedStatus] = useState('');
  const [error, setError] = useState('');

  // Nur Admins haben Zugriff — wait for auth to finish loading first
  useEffect(() => {
    console.log('[UserManagement] authLoading:', authLoading, 'role:', currentUser?.role);
    if (authLoading) return;
    if (currentUser?.role !== 'admin') {
      console.warn('[UserManagement] Kein Admin – Weiterleitung. Aktuelle Rolle:', currentUser?.role);
      navigate('/');
    }
  }, [currentUser, authLoading, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (currentUser?.role === 'admin') {
      console.log('[UserManagement] Admin bestätigt – lade User');
      loadUsers();
    }
  }, [currentUser, authLoading]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStatus = (user) => {
    console.log('Opening edit dialog for user:', user);
    setEditingUser(user);
    setEditedStatus(user.role);
    console.log('Set editedStatus to:', user.role);
  };

  const handleSaveStatus = async () => {
    if (!editingUser) return;

    console.log('Saving role change:', { userId: editingUser.id, newRole: editedStatus });
    try {
      const response = await usersAPI.updateRole(editingUser.id, { role: editedStatus });
      console.log('Response from server:', response.data);
      setUsers(users.map(u =>
        u.id === editingUser.id
          ? { ...u, role: editedStatus }
          : u
      ));
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user role:', error);
      console.error('Error response:', error.response);
      setError('Fehler beim Aktualisieren der Rolle');
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await usersAPI.delete(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Fehler beim Löschen des Benutzers');
      setDeleteDialogOpen(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'moderator':
        return 'bg-orange-100 text-orange-800';
      case 'user':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'admin':
        return 'Administrator';
      case 'moderator':
        return 'Moderator';
      case 'user':
      default:
        return 'User';
    }
  };

  if (loading) {
    return <div className="user-management-page">Lädt...</div>;
  }

  return (
    <div className="user-management-page">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4">Benutzerverwaltung</h1>
      <p className="text-sm sm:text-base text-gray-600 mb-6">
        Verwalten Sie alle registrierten Benutzer
      </p>

      {error && (
        <div className="error-message mb-4">
          {error}
        </div>
      )}

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Users size={20} className="text-blue-600" />
            <h2 className="text-xl font-semibold">Alle Benutzer ({users.length})</h2>
          </div>

          {/* Desktop Table View */}
          <table className="activities-table activities-table-desktop">
            <thead>
              <tr>
                <th>Vorname</th>
                <th>Name</th>
                <th>E-Mail-Adresse</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.vorname || '-'}</td>
                  <td>{user.name || '-'}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeColor(user.role)}`}>
                      {getStatusLabel(user.role)}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <Button
                      variant="outline"
                      onClick={() => handleEditStatus(user)}
                      className="btn-sm btn-edit"
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDeleteClick(user)}
                      className="btn-sm btn-delete"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile Card View */}
          <div className="activities-cards-mobile space-y-3">
            {users.map((user) => (
              <Card key={user.id} className="p-3" style={{ minHeight: 'auto' }}>
                <div className="space-y-2">
                  <div className="flex gap-2 text-xs" style={{ flexWrap: 'wrap' }}>
                    <span className="font-medium">{user.vorname || ''} {user.name || user.username}</span>
                    <span>•</span>
                    <span className="text-gray-600">{user.email}</span>
                  </div>
                  <div className="flex gap-2 items-center pt-2 border-t justify-between">
                    <span className={`status-badge ${getStatusBadgeColor(user.role)}`} style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                      {getStatusLabel(user.role)}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => handleEditStatus(user)}
                        className="btn-sm btn-edit"
                        style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDeleteClick(user)}
                        className="btn-sm btn-delete"
                        style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>

      {/* Edit Status Dialog */}
      {editingUser && (
        <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Status bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie den Status von {editingUser.vorname || editingUser.username} {editingUser.name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="status">Status</Label>
              <Select 
                id="status" 
                value={editedStatus} 
                onValueChange={(value) => {
                  console.log('Select onValueChange called with:', value);
                  setEditedStatus(value);
                  console.log('editedStatus updated to:', value);
                }}
                className="mt-2"
              >
                <option value="user">User</option>
                <option value="moderator">Moderator</option>
                <option value="admin">Administrator</option>
              </Select>
              <p className="text-sm text-gray-500 mt-2">
                Aktueller Wert: {editedStatus}
              </p>
            </div>
            <div className="dialog-footer">
              <Button variant="outline" onClick={() => setEditingUser(null)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveStatus}>Speichern</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Benutzer löschen</DialogTitle>
              <DialogDescription>
                Möchten Sie den Benutzer {userToDelete?.vorname || userToDelete?.username} {userToDelete?.name} wirklich
                löschen? Diese Aktion kann nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <div className="dialog-footer">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button className="btn-delete-confirm" onClick={handleConfirmDelete}>
                Löschen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UserManagement;
